import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { enhancedApi } from '../services/api';
import { useNotifications } from '../contexts/NotificationContext';
import { 
  X, 
  Users, 
  User, 
  Calendar, 
  AlertCircle, 
  CheckCircle,
  Plus,
  Trash2,
  Clock,
  Save,
  UserPlus,
  CheckSquare
} from 'lucide-react';

interface AssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCases: number[];
  caseType: 'mortality' | 'morbidity';
  onAssignmentComplete: () => void;
}

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  department_name: string;
  unit_name: string;
  is_active: boolean;
}

interface AssignmentData {
  assignment_type: 'individual' | 'team' | 'multi_individual';
  auditor_id?: number;
  team_id?: number;
  reviewer_id?: number;
  due_date: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  notes: string;
  // Multi-auditor fields
  auditors?: Array<{
    auditor_id: number;
    role: 'primary' | 'secondary';
    notes?: string;
  }>;
  // Multi-reviewer fields
  reviewers?: Array<{
    reviewer_id: number;
    role: 'primary' | 'secondary';
    notes?: string;
  }>;
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({
  isOpen,
  onClose,
  selectedCases,
  caseType,
  onAssignmentComplete
}) => {
  const { addAssignmentNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [auditors, setAuditors] = useState<User[]>([]);
  const [reviewers, setReviewers] = useState<User[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [assignmentData, setAssignmentData] = useState<AssignmentData>({
    assignment_type: 'individual',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    priority: 'medium',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchTeams();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const response = await enhancedApi.users.getUsers();
      if (response.success && 'data' in response) {
        const users = response.data;
        // Auditors can be: superuser, auditor, or reviewer
        setAuditors(users.filter((user: User) => 
          (user.role === 'superuser' || user.role === 'auditor' || user.role === 'reviewer') && user.is_active
        ));
        // Reviewers can be: reviewer or superuser
        setReviewers(users.filter((user: User) => 
          (user.role === 'reviewer' || user.role === 'superuser') && user.is_active
        ));
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await enhancedApi.teams.getTeams();
      if (response.success) {
        setTeams(response.data || []);
      } else {
        console.error('Failed to fetch teams:', response.message);
        setTeams([]);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setTeams([]);
    }
  };

  // Validation functions
  const validateNoDuplicateAuditors = () => {
    if (assignmentData.assignment_type === 'multi_individual' && assignmentData.auditors) {
      const auditorIds = assignmentData.auditors.map(a => a.auditor_id);
      const uniqueIds = new Set(auditorIds);
      if (auditorIds.length !== uniqueIds.size) {
        alert('Each auditor can only be assigned once per assignment');
        return false;
      }
    }
    return true;
  };

  const validateNoDuplicateReviewers = () => {
    if (assignmentData.reviewers) {
      const reviewerIds = assignmentData.reviewers.map(r => r.reviewer_id);
      const uniqueIds = new Set(reviewerIds);
      if (reviewerIds.length !== uniqueIds.size) {
        alert('Each reviewer can only be assigned once per assignment');
        return false;
      }
    }
    return true;
  };

  const validateNoAuditorReviewerOverlap = () => {
    if (assignmentData.assignment_type === 'multi_individual' && assignmentData.auditors && assignmentData.reviewers) {
      const auditorIds = assignmentData.auditors.map(a => a.auditor_id);
      const reviewerIds = assignmentData.reviewers.map(r => r.reviewer_id);
      const overlap = auditorIds.filter(id => reviewerIds.includes(id));
      if (overlap.length > 0) {
        alert('A person cannot be both an auditor and reviewer for the same assignment');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (assignmentData.assignment_type === 'individual' && !assignmentData.auditor_id) {
      alert('Please select an auditor for individual assignment');
      return;
    }
    
    if (assignmentData.assignment_type === 'team' && !assignmentData.team_id) {
      alert('Please select a team for team assignment');
      return;
    }

    if (assignmentData.assignment_type === 'multi_individual') {
      if (!assignmentData.auditors || assignmentData.auditors.length === 0) {
        alert('Please add at least one auditor for multi-individual assignment');
        return;
      }
      
      if (assignmentData.auditors.some(a => a.auditor_id === 0)) {
        alert('Please select all auditors');
        return;
      }

      if (assignmentData.auditors.filter(a => a.role === 'primary').length !== 1) {
        alert('Please assign exactly one primary auditor');
        return;
      }
      
      // Validate no duplicate auditors
      if (!validateNoDuplicateAuditors()) {
        return;
      }
      
      // Validate no duplicate reviewers
      if (!validateNoDuplicateReviewers()) {
        return;
      }
      
      // Validate no auditor-reviewer overlap
      if (!validateNoAuditorReviewerOverlap()) {
        return;
      }
    }

    if (!assignmentData.reviewer_id) {
      alert('Please select a reviewer for the assignment');
      return;
    }

    try {
      setLoading(true);
      
      const response = await enhancedApi.admin.assignAudits({
        case_ids: selectedCases,
        case_type: caseType,
        assignment_type: assignmentData.assignment_type,
        auditor_id: assignmentData.auditor_id,
        team_id: assignmentData.team_id,
        reviewer_id: assignmentData.reviewer_id,
        due_date: assignmentData.due_date,
        priority: assignmentData.priority,
        notes: assignmentData.notes,
        // Multi-auditor data
        auditors: assignmentData.auditors,
        // Multi-reviewer data
        reviewers: assignmentData.reviewers
      });

      if (response.success) {
        // Trigger assignment notifications for each case
        selectedCases.forEach((caseId, index) => {
          let assignedToName = '';
          if (assignmentData.assignment_type === 'individual') {
            const auditor = auditors.find(a => a.id === assignmentData.auditor_id);
            assignedToName = auditor ? `${auditor.first_name} ${auditor.last_name}` : '';
          } else if (assignmentData.assignment_type === 'team') {
            assignedToName = teams.find(t => t.id === assignmentData.team_id)?.name || '';
          } else if (assignmentData.assignment_type === 'multi_individual') {
            const auditorNames = assignmentData.auditors?.map(a => {
              const auditor = auditors.find(au => au.id === a.auditor_id);
              return auditor ? `${auditor.first_name} ${auditor.last_name} (${a.role})` : '';
            }).filter(name => name).join(', ') || '';
            assignedToName = `Multiple Auditors: ${auditorNames}`;
          }
          
          addAssignmentNotification({
            assignmentId: response.data?.assignments?.[index]?.id || Math.floor(Math.random() * 1000) + 1,
            caseType: caseType,
            caseId: caseId,
            assignedTo: assignedToName || 'Unknown',
            dueDate: assignmentData.due_date,
            priority: assignmentData.priority
          });
        });
        
        alert(`Successfully assigned ${selectedCases.length} cases`);
        onAssignmentComplete();
        onClose();
      } else {
        alert('Failed to assign cases: ' + response.message);
      }
    } catch (error) {
      console.error('Assignment failed:', error);
      alert('Failed to assign cases. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedAuditorName = () => {
    if (assignmentData.assignment_type === 'individual' && assignmentData.auditor_id) {
      const auditor = auditors.find(a => a.id === assignmentData.auditor_id);
      return auditor ? `${auditor.first_name} ${auditor.last_name}` : '';
    }
    return '';
  };

  const getSelectedTeamName = () => {
    if (assignmentData.assignment_type === 'team' && assignmentData.team_id) {
      const team = teams.find(t => t.id === assignmentData.team_id);
      return team ? team.name : '';
    }
    return '';
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Assign Audit Cases</h2>
            <p className="text-sm text-gray-600 mt-1">
              Assigning {selectedCases.length} {caseType} case{selectedCases.length > 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Assignment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Assignment Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                assignmentData.assignment_type === 'individual' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="assignment_type"
                  value="individual"
                  checked={assignmentData.assignment_type === 'individual'}
                  onChange={(e) => setAssignmentData(prev => ({ 
                    ...prev, 
                    assignment_type: e.target.value as 'individual',
                    team_id: undefined,
                    auditors: undefined
                  }))}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">Individual Assignment</div>
                    <div className="text-sm text-gray-500">Assign to a single auditor</div>
                  </div>
                </div>
              </label>

              <label className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                assignmentData.assignment_type === 'team' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="assignment_type"
                  value="team"
                  checked={assignmentData.assignment_type === 'team'}
                  onChange={(e) => setAssignmentData(prev => ({ 
                    ...prev, 
                    assignment_type: e.target.value as 'team',
                    auditor_id: undefined,
                    auditors: undefined
                  }))}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">Team Assignment</div>
                    <div className="text-sm text-gray-500">Assign to an audit team</div>
                  </div>
                </div>
              </label>

              <label className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                assignmentData.assignment_type === 'multi_individual' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="assignment_type"
                  value="multi_individual"
                  checked={assignmentData.assignment_type === 'multi_individual'}
                  onChange={(e) => setAssignmentData(prev => ({ 
                    ...prev, 
                    assignment_type: e.target.value as 'multi_individual',
                    auditor_id: undefined,
                    team_id: undefined,
                    auditors: [{ auditor_id: 0, role: 'primary' }]
                  }))}
                  className="sr-only"
                />
                <div className="flex items-center space-x-3">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900">Multi-Individual</div>
                    <div className="text-sm text-gray-500">Assign to multiple individual auditors</div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Auditor/Team Selection */}
          {assignmentData.assignment_type === 'individual' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Auditor *
              </label>
              <select
                value={assignmentData.auditor_id || ''}
                onChange={(e) => setAssignmentData(prev => ({ 
                  ...prev, 
                  auditor_id: parseInt(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Choose an auditor...</option>
                {auditors.map(auditor => (
                  <option key={auditor.id} value={auditor.id}>
                    {auditor.first_name} {auditor.last_name} - {auditor.department_name} ({auditor.role})
                  </option>
                ))}
              </select>
            </div>
          ) : assignmentData.assignment_type === 'team' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Team *
              </label>
              <select
                value={assignmentData.team_id || ''}
                onChange={(e) => setAssignmentData(prev => ({ 
                  ...prev, 
                  team_id: parseInt(e.target.value) 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Choose a team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.member_count} members)
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Multi-Auditor Assignment *
                </label>
                <button
                  type="button"
                  onClick={() => setAssignmentData(prev => ({
                    ...prev,
                    auditors: [...(prev.auditors || []), { auditor_id: 0, role: 'secondary' }]
                  }))}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Auditor</span>
                </button>
              </div>

              <div className="space-y-4">
                {(assignmentData.auditors || []).map((auditor, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Auditor {index + 1}
                        </span>
                        {auditor.role === 'primary' && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            Primary
                          </span>
                        )}
                      </div>
                      {(assignmentData.auditors || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => setAssignmentData(prev => ({
                            ...prev,
                            auditors: (prev.auditors || []).filter((_, i) => i !== index)
                          }))}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Auditor *
                        </label>
                        <select
                          value={auditor.auditor_id}
                          onChange={(e) => setAssignmentData(prev => ({
                            ...prev,
                            auditors: (prev.auditors || []).map((a, i) => 
                              i === index ? { ...a, auditor_id: parseInt(e.target.value) } : a
                            )
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value={0}>Choose an auditor...</option>
                          {auditors.map(aud => (
                            <option key={aud.id} value={aud.id}>
                              {aud.first_name} {aud.last_name} - {aud.department_name} ({aud.role})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Role *
                        </label>
                        <select
                          value={auditor.role}
                          onChange={(e) => setAssignmentData(prev => ({
                            ...prev,
                            auditors: (prev.auditors || []).map((a, i) => 
                                i === index ? { ...a, role: e.target.value as 'primary' | 'secondary' } : a
                            )
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="primary">Primary Auditor</option>
                          <option value="secondary">Secondary Auditor</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={auditor.notes || ''}
                        onChange={(e) => setAssignmentData(prev => ({
                          ...prev,
                          auditors: (prev.auditors || []).map((a, i) => 
                            i === index ? { ...a, notes: e.target.value } : a
                          )
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={2}
                        placeholder="Specific instructions for this auditor..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Multi-Reviewer Selection (only for multi-individual assignments) */}
          {assignmentData.assignment_type === 'multi_individual' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Multi-Reviewer Assignment *
                </label>
                <button
                  type="button"
                  onClick={() => setAssignmentData(prev => ({
                    ...prev,
                    reviewers: [...(prev.reviewers || []), { reviewer_id: 0, role: 'secondary' }]
                  }))}
                  className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Reviewer</span>
                </button>
              </div>

              <div className="space-y-4">
                {(assignmentData.reviewers || []).map((reviewer, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4 bg-green-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <CheckSquare className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-700">
                          Reviewer {index + 1}
                        </span>
                        {reviewer.role === 'primary' && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                            Primary
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setAssignmentData(prev => ({
                          ...prev,
                          reviewers: (prev.reviewers || []).filter((_, i) => i !== index)
                        }))}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select Reviewer *
                        </label>
                        <select
                          value={reviewer.reviewer_id}
                          onChange={(e) => {
                            const selectedReviewerId = parseInt(e.target.value);
                            setAssignmentData(prev => ({
                              ...prev,
                              reviewers: (prev.reviewers || []).map((r, i) => 
                                i === index ? { ...r, reviewer_id: selectedReviewerId } : r
                              )
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value={0}>Select a reviewer...</option>
                          {reviewers.map((reviewerUser) => (
                            <option key={reviewerUser.id} value={reviewerUser.id}>
                              {reviewerUser.first_name} {reviewerUser.last_name} - {reviewerUser.department_name} ({reviewerUser.role})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Role *
                        </label>
                        <select
                          value={reviewer.role}
                          onChange={(e) => {
                            setAssignmentData(prev => ({
                              ...prev,
                              reviewers: (prev.reviewers || []).map((r, i) => 
                                i === index ? { ...r, role: e.target.value as 'primary' | 'secondary' } : r
                              )
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="secondary">Secondary</option>
                          <option value="primary">Primary</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={reviewer.notes || ''}
                        onChange={(e) => {
                          setAssignmentData(prev => ({
                            ...prev,
                            reviewers: (prev.reviewers || []).map((r, i) => 
                              i === index ? { ...r, notes: e.target.value } : r
                            )
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        rows={2}
                        placeholder="Specific instructions for this reviewer..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviewer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigning Reviewer *
            </label>
            <select
              value={assignmentData.reviewer_id || ''}
              onChange={(e) => setAssignmentData(prev => ({ 
                ...prev, 
                reviewer_id: e.target.value ? parseInt(e.target.value) : undefined 
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Choose a reviewer...</option>
              {reviewers.map(reviewer => (
                <option key={reviewer.id} value={reviewer.id}>
                  {reviewer.first_name} {reviewer.last_name} - {reviewer.department_name}
                </option>
              ))}
            </select>
          </div>

          {/* Due Date and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="date"
                  value={assignmentData.due_date}
                  onChange={(e) => setAssignmentData(prev => ({ ...prev, due_date: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority *
              </label>
              <select
                value={assignmentData.priority}
                onChange={(e) => setAssignmentData(prev => ({ 
                  ...prev, 
                  priority: e.target.value as 'low' | 'medium' | 'high' | 'critical' 
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Notes
            </label>
            <textarea
              value={assignmentData.notes}
              onChange={(e) => setAssignmentData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Add any specific instructions or notes for this assignment..."
            />
          </div>

          {/* Assignment Summary */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-gray-900 mb-3">Assignment Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Cases:</span>
                <span className="font-medium">{selectedCases.length} {caseType} case{selectedCases.length > 1 ? 's' : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium capitalize">{assignmentData.assignment_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Assigned to:</span>
                <span className="font-medium">
                  {assignmentData.assignment_type === 'individual' 
                    ? getSelectedAuditorName() 
                    : assignmentData.assignment_type === 'team'
                    ? getSelectedTeamName()
                    : assignmentData.auditors?.length 
                    ? `${assignmentData.auditors.length} Auditors (${assignmentData.auditors.filter(a => a.role === 'primary').length} Primary)`
                    : 'No auditors selected'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Due Date:</span>
                <span className="font-medium">{new Date(assignmentData.due_date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Priority:</span>
                <span className={`font-medium capitalize px-2 py-1 rounded-full text-xs ${
                  assignmentData.priority === 'critical' ? 'bg-red-100 text-red-800' :
                  assignmentData.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                  assignmentData.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {assignmentData.priority}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Assigning...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Assign Cases</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
